import { useEffect, useState } from 'react';

import { SpinnerRoundFilled as Spinner } from 'spinners-react';
import { Box, Typography, Grid, Container, Modal, Button } from '@mui/material';
import { toast } from 'react-toast';
//
import EnsCard from '../common/EnsCard';
import Header from '../Header/Header';
import Footer from '../common/Footer';
import PendingTx from '../Home/components/PendingTx';
import Title from '../common/Title';

import useEnsForge from '../../hooks/useEnsForge';
import useAppCtx from '../../hooks/useAppCtx';
import useEnsTokens from '../../hooks/useEnsTokens';
import useWeb3Ctx from '../../hooks/useWeb3Ctx';
//
import { mapRanges } from '../../utils';
import title from '../../assets/images/title.png';

const sx = {
  root: {
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    backgroundSize: 'cover',
    position: 'relative',
    transition: 'all .3s',
    pt: 'calc(100%-140px)',
    mt: '60px',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  btn: {
    mx: 'auto',
    my: 4,
    fontSize: '20px',
    width: '180px',
    fontFamily: 'boucherie-block',
    borderRadius: '0px',
    minHeight: '65px',
    border: '2px solid #020100',
    backgroundColor: '#020100',
    backgroundClip: 'content-box',
    padding: '5px',
    '&:hover': {
      border: '2px solid #9D4C10',
      backgroundColor: '#9D4C10',
    },
    '&:active': {
      border: '2px solid #6A330A',
      backgroundColor: '#6A330A',
    },
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  modalContent: {
    bgcolor: 'background.paper',
    p: 2,
    minWidth: '90vw',
    minHeight: 'auto',
    borderRadius: '10px',
    maxHeight: '90vh',
    overflow: 'auto',
    textAlign: 'center',
  },
  darkOverlay: {
    position: 'fixed',
    zIndex: 1000,
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.8)',
  },
};

const seriesTokenIdRangesMapping = {
  'summer-maiden': mapRanges('1-144'),
  'summer-knight': mapRanges('145-288'),
  'summer-elf': mapRanges('289-432'),
  'winter-knight': mapRanges('433-576'),
  'winter-elf': mapRanges('577-720'),
  'winter-queen': mapRanges('721-864'),
  'winter-goblin': mapRanges('865-1008'),
  'winter-crone': mapRanges('1009-1152'),
  'summer-queen': mapRanges('1153-1296'),
  'summer-goblin': mapRanges('1297-1440'),
  'summer-crone': mapRanges('1441-1584'),
  'winter-maiden': mapRanges('1585-1728'),
};

const cardNames = {
  'summer-maiden': 'Summer Maiden',
  'summer-knight': 'Summer Knight',
  'summer-elf': 'Summer Elf',
  'winter-knight': 'Winter Knight',
  'winter-elf': 'Winter Elf',
  'winter-queen': 'Winter Queen',
  'winter-goblin': 'Winter Goblin',
  'winter-crone': 'Winter Crone',
  'summer-queen': 'Summer Queen',
  'summer-goblin': 'Summer Goblin',
  'summer-crone': 'Summer Crone',
  'winter-maiden': 'Winter Maiden',
};

const getSeriesIndex = (seriesName) => {
  const seriesNames = Object.keys(seriesTokenIdRangesMapping);
  const index = seriesNames.indexOf(seriesName);
  return index;
};
const isSeriesSelected = (series, selectedIds) => !!selectedIds[series];
const isSeriesIdSelected = (series, id, selectedIds) => {
  return selectedIds[series] === id;
};

const EligibleText = ({ idCount }) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        textAlign: 'center',
        left: 0,
        right: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            background: 'black',
            borderRadius: '10px',
            color: 'white',
            px: 1,
          }}
        >
          {idCount} Eligible cards
        </Typography>
      </Box>
    </Box>
  );
};

function ForgeCardUI() {
  const { address } = useWeb3Ctx();
  const [selectedIds, setSelectedIds] = useState({});
  const [activeSeries, setActiveSeries] = useState('summer-maiden');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { forgeAll, approvePending, txInProgress } = useEnsForge();
  const { metadata, metadataLoaded, loading } = useAppCtx();
  const { getTokens } = useEnsTokens();
  const [tokenIds, setTokenIds] = useState([]);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (address && !metadataLoaded) {
      getTokens();
    }
  }, [address, metadataLoaded]);

  useEffect(() => {
    setTokenIds(
      metadata.filter((data) => data.claimable).map((data) => data.id)
    );
  }, [metadata]);

  useEffect(() => {
    let updatedSelectedIds = { ...selectedIds };
    Object.keys(seriesTokenIdRangesMapping).forEach((series) => {
      let firstCard = seriesIds(series)[0];
      updatedSelectedIds[series] = firstCard;
    });
    setSelectedIds(updatedSelectedIds);
  }, [tokenIds, loading]);

  const handleOpenModal = (series) => {
    if (seriesIds(series).length === 0) return;
    setActiveSeries(series);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSelectId = (id) => {
    const updatedSelectedIds = { ...selectedIds };
    updatedSelectedIds[activeSeries] = id;
    setSelectedIds(updatedSelectedIds);
    handleCloseModal();
  };

  const areAllSeriesSelected = () => {
    const seriesNames = Object.keys(seriesTokenIdRangesMapping);
    return seriesNames.every((series) => !!selectedIds[series]);
  };

  const seriesIds = (series) => {
    return tokenIds.filter((id) => {
      return seriesTokenIdRangesMapping[series].includes(id);
    });
  };

  const forgeIds = async () => {
    if (!areAllSeriesSelected()) {
      toast.warn('Select a card from each series first!');
      return;
    }

    const selectedTokenIds = Object.values(selectedIds).sort((a, b) => a - b);
    // console.log(selectedIds);
    // console.log(selectedTokenIds);
    await forgeAll(selectedTokenIds);
  };
  return (
    <>
      <Header />
      <Box sx={sx.imgContainer}>
        {/* <img
          src={title}
          style={{ width: '100%', marginTop: '0px' }}
          alt="title_decortion"
        /> */}
      </Box>
      <Title text="Forge" style={{ width: '25%' }} />
      {!txInProgress && (
        <Container sx={sx.root}>
          {!loading && (
            <Grid container spacing={2} alignItems={'stretch'} display={'flex'}>
              {Object.keys(seriesTokenIdRangesMapping).map((series, index) => (
                <EnsCard
                  key={series}
                  seriesId={index + 1}
                  name={cardNames[series]}
                  md={2}
                  sm={3}
                  xs={4}
                  titleFontSize={24}
                  sxImage={{
                    opacity: isSeriesSelected(series, selectedIds) ? 1 : 0.5,
                  }}
                  onClick={() => handleOpenModal(series)}
                  innerId={selectedIds[series]}
                  renderExtra={
                    <>
                      {!isSeriesSelected(series, selectedIds) && (
                        <EligibleText idCount={seriesIds(series).length} />
                      )}
                    </>
                  }
                  isSeriesSelected={isSeriesIdSelected}
                  isForge={true}
                />
              ))}
            </Grid>
          )}

          {loading && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Spinner color="black" />
              <Typography varian="body1" style={{ maxWidth: '728px' }}>
                Loading your Tokens..
              </Typography>
            </Box>
          )}

          <Button
            variant="outlined"
            sx={sx.btn}
            onClick={() => forgeIds()}
            disabled={!address || loading}
          >
            Forge
          </Button>

          {approvePending && <Box sx={sx.darkOverlay} />}
        </Container>
      )}

      <Modal open={isModalOpen} onClose={handleCloseModal} sx={sx.modal}>
        <Box sx={sx.modalContent}>
          <Typography variant="heading2" color={'black'} sx={{ pb: '10px' }}>
            Select an ID
          </Typography>
          <Grid
            container
            spacing={2}
            sx={{ justifyContent: 'center', px: '10px' }}
          >
            {seriesIds(activeSeries).map((id) => {
              return (
                <EnsCard
                  key={id}
                  seriesId={getSeriesIndex(activeSeries) + 1}
                  id={id}
                  md={2}
                  xs={6}
                  sm={3}
                  titleFontSize={30}
                  onClick={() => handleSelectId(id)}
                  sxImageContainer={
                    isSeriesIdSelected(activeSeries, id, selectedIds)
                      ? {
                          border: `2px solid #81c784`,
                          borderRadius: '10px',
                        }
                      : { border: '2px solid transparent' }
                  }
                />
              );
            })}
          </Grid>
        </Box>
      </Modal>

      {txInProgress && <PendingTx />}

      <Footer />
    </>
  );
}

export default ForgeCardUI;
